/**
 * pifeeder-app bridge
 *
 * Flutter (`flutter_inappwebview`) — or any WebView — pushes live ADS-B data
 * into the page by calling these globals after the page loads.
 *
 *   window.pifeederBridge.injectFlights([{ id, call, lat, lon, hdg, alt, spd, type? }])
 *   window.pifeederBridge.selectFlight("ANA269")
 *   window.pifeederBridge.injectSignal({ online: true, dB: 47.4, trackedCount: 17 })
 *
 * When the resolved $source is `dev-mock` (`vite dev` only), `dev-mock.ts`
 * is dynamically imported and drives the planes/signal stores directly.
 * Production builds never enter that branch (see discover.ts) and the
 * dev-mock module is tree-shaken out of the release bundle entirely.
 */

import { getCachedAircraft, lookupAircraftByHex } from "./aircraft-lookup";
import { type AnyAircraftJson, parseAircraftJson } from "./aircraft-parse";
import { $source } from "./discover";
import {
  $pinned,
  $planes,
  $selectedId,
  $signal,
  $station,
  recordTrails,
  replacePlanes,
  selectPlane,
  upsertPlane,
} from "./store";
import type { LivePlane, SignalStatus } from "./types";

interface PifeederBridge {
  injectFlights(planes: LivePlane[]): void;
  upsertFlight(plane: LivePlane): void;
  selectFlight(id: string | null): void;
  injectSignal(s: Partial<SignalStatus>): void;
}

declare global {
  interface Window {
    pifeederBridge: PifeederBridge;
    __pifeederIsApp?: boolean;
  }
}

export function installBridge() {
  window.pifeederBridge = {
    injectFlights(planes) {
      replacePlanes(planes);
      recordTrails(planes);
    },
    upsertFlight(p) {
      upsertPlane(p);
      recordTrails([p]);
    },
    selectFlight(id) {
      // Respect pin: external auto-selects don't override a pinned selection
      if ($pinned.get() && $selectedId.get() && id !== $selectedId.get()) return;
      selectPlane(id);
    },
    injectSignal(s) {
      $signal.set({ ...$signal.get(), ...s });
    },
  };

  // Drive the feed based on the resolved Source. Re-evaluates whenever
  // discovery changes (e.g. LAN Pi powers up mid-session).
  let mockTimer: ReturnType<typeof setInterval> | null = null;
  let localTimer: ReturnType<typeof setInterval> | null = null;
  let infoTimer: ReturnType<typeof setInterval> | null = null;

  function stopAll() {
    if (mockTimer) {
      clearInterval(mockTimer);
      mockTimer = null;
    }
    if (localTimer) {
      clearInterval(localTimer);
      localTimer = null;
    }
    if (infoTimer) {
      clearInterval(infoTimer);
      infoTimer = null;
    }
  }

  $source.subscribe((src) => {
    stopAll();
    if (src.kind === "app") {
      // The WebView is loaded from `http://${piIp}:8080/`, so the Pi's
      // ADS-B + info endpoints are same-origin and the SPA polls them
      // directly — the Flutter bridge `injectFlights` API stays available
      // for any future use case but is not driven on a timer (a previous
      // setup where native also polled at 1Hz caused visible flicker as
      // two replacePlanes() calls raced per second).
      const baseUrl = window.location.origin;
      localTimer = setInterval(() => pollLocalAircraft(baseUrl), 1000);
      infoTimer = setInterval(() => pollLocalInfo(baseUrl), 30_000);
      pollLocalAircraft(baseUrl);
      pollLocalInfo(baseUrl);
      return;
    }
    if (src.kind === "local") {
      localTimer = setInterval(() => pollLocalAircraft(src.baseUrl), 1000);
      infoTimer = setInterval(() => pollLocalInfo(src.baseUrl), 30_000);
      pollLocalAircraft(src.baseUrl);
      pollLocalInfo(src.baseUrl);
      return;
    }
    if (src.kind === "dev-mock") {
      // Local `vite dev` only — see discover.ts for the production guard.
      // Dynamic import behind the same DEV constant so the mock module
      // tree-shakes out of release bundles entirely.
      if (import.meta.env.DEV) {
        void import("./dev-mock").then(({ startDevMockFeed }) => {
          mockTimer = startDevMockFeed();
        });
      }
      return;
    }
    // api or mock — leave planes empty. The user explicitly asked to drop
    // the mock feed: when there's no real ADS-B source the map should be
    // empty rather than show fictional ANA32/JAL124 traffic that confuses
    // first-time visitors and lingers for ~1 frame on every reload before
    // discover.ts swaps to the real source.
    replacePlanes([]);
  });
}

// Backpressure flag: when a poll is in flight, the next tick skips
// instead of stacking. Without this, a Pi that briefly takes >1 s
// to serve aircraft.json would build a request queue that never
// drains and only widens the gap to real-time.
// See SCALING_AUDIT.md S-07.
let aircraftPollInflight = false;
let infoPollInflight = false;

async function pollLocalAircraft(baseUrl: string) {
  if (aircraftPollInflight) return;
  aircraftPollInflight = true;
  try {
    const res = await fetch(`${baseUrl}/data/aircraft.json`, { cache: "no-store" });
    if (!res.ok) return;
    const json = (await res.json()) as AnyAircraftJson;
    const planes = parseAircraftJson(json);
    // Synchronously enrich any planes whose hex we've already looked up
    // (cache hit) before pushing to the store, so the detail card sees
    // type/registration on first render.
    for (const p of planes) {
      const cached = getCachedAircraft(p.id);
      if (cached) Object.assign(p, cached);
    }
    replacePlanes(planes);
    recordTrails(planes);
    // Fire-and-forget hexdb.io lookups for the rest. When each lookup
    // resolves, upsertPlane merges the metadata in and the detail card
    // re-renders via $planes subscription.
    for (const p of planes) {
      if (p.type) continue; // already enriched
      void lookupAircraftByHex(p.id).then((info) => {
        if (!info) return;
        const cur = $planes.get()[p.id];
        if (!cur) return; // plane left the feed before the lookup landed
        upsertPlane({ ...cur, ...info });
      });
    }
  } catch {
    // network blip — try again next tick
  } finally {
    aircraftPollInflight = false;
  }
}

async function pollLocalInfo(baseUrl: string) {
  if (infoPollInflight) return;
  infoPollInflight = true;
  try {
    const res = await fetch(`${baseUrl}/pifeeder/info`, { cache: "no-store" });
    if (!res.ok) return;
    const info = (await res.json()) as {
      signal?: Partial<SignalStatus>;
      station?: { lat?: number | null; lon?: number | null };
    };
    if (info.signal) {
      $signal.set({ ...$signal.get(), ...info.signal });
    }
    // Without this the SPA stays at the hard-coded Setagaya default and
    // the user sees their station marker pinned to the wrong city even
    // though /pifeeder/info reports the real receiver coordinates. Only
    // overwrite if we have a numeric pair AND the current value is the
    // baked-in default — `geo` (browser geolocation) takes priority once
    // the user grants it.
    if (info.station && typeof info.station.lat === "number" && typeof info.station.lon === "number") {
      const cur = $station.get();
      if (cur.source === "default") {
        $station.set({
          lat: info.station.lat,
          lon: info.station.lon,
          source: "pi",
        });
      }
    }
  } catch {
    // ignore
  } finally {
    infoPollInflight = false;
  }
}
